/**
 * ConsentClient, posts consent decisions to the collect-consent Edge Function.
 */

import type { ConsentDecisions } from "../core/StorageAdapter";
import type { GeoResult } from "../core/GeoDetector";

export interface RecordOptions {
  /**
   * The decision was made on the built-in fallback banner, not on a published
   * config. Sent with the sentinel bannerVersion 0 so the record is labelled as
   * such and never attributed to a real banner version.
   */
  fallback?: boolean;
  /**
   * Let the request outlive the page. Set on withdrawal, where the page
   * reloads right after, so the refusal reaches the server even if the reload
   * wins the race.
   */
  keepalive?: boolean;
}

/** Page URL without query string or fragment, which can carry personal data. */
export function recordablePageUrl(href: string): string {
  try {
    const url = new URL(href);
    return `${url.origin}${url.pathname}`;
  } catch {
    return "";
  }
}

/**
 * Fixed pb:error code for a consent record the collector answered with a
 * non-2xx status. The detail carries only the code and the status, never the
 * decisions, the session id or the page URL.
 */
export const CONSENT_RECORD_REJECTED = "consent_record_rejected";

/**
 * Refusal codes collect-consent sends, with what the site owner should do.
 * Only these are surfaced: the script never echoes an unknown server string.
 * domain_unverified is the common one, a tag installed before DNS
 * verification finished (test.success.jeppesenforeflight.com, 6 Oct 2026).
 */
const REFUSAL_HINTS: Record<string, string> = {
  domain_unverified:
    "This domain is not verified in Pritect Beacon, so consent is not recorded. " +
    "Complete DNS verification for this domain in the Pritect Beacon dashboard.",
  domain_inactive: "This domain is not active in Pritect Beacon, so consent is not recorded.",
  domain_not_found:
    "Pritect Beacon does not know this data-domain-id. Re-copy the snippet from the dashboard.",
};

/** The collector's refusal code, when it is one the script knows. */
async function refusalReason(res: Response): Promise<string | null> {
  try {
    const body = (await res.json()) as { code?: unknown };
    return typeof body?.code === "string" && body.code in REFUSAL_HINTS ? body.code : null;
  } catch {
    return null;
  }
}

/**
 * Make a refused record observable. Before this the only trace was a console
 * line, so a collector answering 503 for weeks (its HMAC secret unset) looked
 * exactly like a healthy one to the site owner. Never throws: the visitor's
 * choice is already applied locally and must not depend on this.
 */
function reportRecordRejected(status: number, reason: string | null): void {
  const base = `Consent record was not stored (HTTP ${status}).`;
  const message = reason ? `${base} ${REFUSAL_HINTS[reason]}` : base;
  try {
    console.warn(`[Pritect Beacon] ${message}`);
    window.dispatchEvent(
      new CustomEvent("pb:error", {
        detail: { code: CONSENT_RECORD_REJECTED, status, ...(reason ? { reason } : {}), message },
      }),
    );
  } catch {
    /* no-op */
  }
}

/**
 * Fixed pb:error code for a record the collector stored but flagged because
 * this page's host is not the site the tag's data-domain-id belongs to: the
 * snippet was copied onto the wrong site. Detail carries the host and the
 * domain id, both already public in the page and its tag.
 */
export const CONSENT_DOMAIN_MISMATCH = "consent_domain_mismatch";

let domainMismatchReported = false;

/**
 * Say so once per page view. Before this, a site loading another domain's tag
 * showed that domain's banner and nothing told its owner. Never throws.
 */
function reportDomainMismatch(host: string, domainId: string): void {
  if (domainMismatchReported) return;
  domainMismatchReported = true;
  const message =
    `This page (${host}) loads Pritect Beacon with data-domain-id ${domainId}, which belongs to a ` +
    "different site. Visitors here see that site's banner and their consent is recorded against it. " +
    "Replace the tag with the snippet from this site's own domain in Pritect Beacon.";
  try {
    console.warn(`[Pritect Beacon] ${message}`);
    window.dispatchEvent(
      new CustomEvent("pb:error", { detail: { code: CONSENT_DOMAIN_MISMATCH, host, domainId, message } }),
    );
  } catch {
    /* no-op */
  }
}

/** For tests: forget that the mismatch was already reported on this page. */
export function resetDomainMismatchReportForTests(): void {
  domainMismatchReported = false;
}

export class ConsentClient {
  private apiUrl: string;
  private domainId: string;

  constructor(apiUrl: string, domainId: string) {
    this.apiUrl = apiUrl;
    this.domainId = domainId;
  }

  /** Resolves true when the server stored the record. Never rejects. */
  async record(
    sessionId: string,
    decisions: ConsentDecisions,
    geo: GeoResult,
    bannerVersion: number,
    tcString?: string,
    options: RecordOptions = {}
  ): Promise<boolean> {
    try {
      const pageUrl = recordablePageUrl(window.location.href);
      const res = await fetch(`${this.apiUrl}/functions/v1/collect-consent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          domainId: this.domainId,
          sessionId,
          decisions,
          regulation: geo.regulation,
          country: geo.country,
          ...(pageUrl ? { pageUrl } : {}),
          bannerVersion,
          ...(options.fallback ? { bannerFallback: true } : {}),
          ...(tcString ? { tcString } : {}),
        }),
        // Only on the withdrawal path, which awaits this promise before it
        // reloads. keepalive on every fire-and-forget request was dropped
        // earlier because abort timing produced partial bodies; here the
        // caller waits for the response, keepalive is just the backstop.
        ...(options.keepalive ? { keepalive: true } : {}),
      });
      if (!res.ok) {
        reportRecordRejected(res.status, await refusalReason(res));
        return false;
      }
      await this.checkDomainMismatch(res);
      return true;
    } catch {
      // Never block the user experience on the record.
      return false;
    }
  }

  /** The collector answers { originMismatch: host } when it flagged the record. */
  private async checkDomainMismatch(res: Response): Promise<void> {
    try {
      const body = (await res.json()) as { originMismatch?: unknown };
      if (typeof body?.originMismatch === "string" && body.originMismatch) {
        reportDomainMismatch(body.originMismatch, this.domainId);
      }
    } catch {
      // An older collector, or an empty body: nothing to report.
    }
  }
}
